import { useState } from "react";
import { HelpCircle, BookOpen, Wrench, Package, Cpu, Settings as SettingsIcon, AlertCircle, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const CATEGORY_ICONS = {
  "Getting Started": BookOpen,
  "Core Features": Package,
  "Tools": Wrench,
  "Production": Package,
  "AI Features": Cpu,
  "System & Settings": SettingsIcon,
  "Troubleshooting": AlertCircle,
};

const CATEGORY_COLORS = {
  "Getting Started": "from-primary/20 to-primary/5",
  "Core Features": "from-blue-500/20 to-blue-500/5",
  "Tools": "from-green-500/20 to-green-500/5",
  "Production": "from-purple-500/20 to-purple-500/5",
  "AI Features": "from-pink-500/20 to-pink-500/5",
  "System & Settings": "from-orange-500/20 to-orange-500/5",
  "Troubleshooting": "from-red-500/20 to-red-500/5",
};

export default function Help() {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["help-articles", searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("help_articles")
        .select("*")
        .order("order_index");

      if (searchQuery) {
        query = query.or(
          `title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%,summary.ilike.%${searchQuery}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const featuredArticles = articles.filter((a) => a.is_featured).slice(0, 6);
  const categories = [...new Set(articles.map((a) => a.category))];

  const getCategoryArticleCount = (category: string) => {
    return articles.filter((a) => a.category === category).length;
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4 py-8">
        <div className="flex justify-center mb-4">
          <div className="p-4 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full">
            <HelpCircle className="h-12 w-12 text-primary" />
          </div>
        </div>
        <h1 className="text-4xl font-bold">Help Center</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Everything you need to know about RoofGenius
        </p>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-lg"
            />
          </div>
        </div>

        {/* Ask Bridgette CTA */}
        <div className="pt-4">
          <Link to="/agent">
            <Button variant="outline" size="lg">
              <Cpu className="mr-2 h-4 w-4" />
              Or Ask Bridgette
            </Button>
          </Link>
        </div>
      </div>

      {/* Featured Articles */}
      {!searchQuery && featuredArticles.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">Quick Start Guides</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredArticles.map((article) => (
              <Link key={article.id} to={`/help/${article.slug}`}>
                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <Badge className="mb-3">{article.category}</Badge>
                  <h3 className="text-lg font-semibold mb-2">{article.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {article.summary}
                  </p>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Categories or Search Results */}
      {searchQuery ? (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">
            {articles.length} {articles.length === 1 ? "Result" : "Results"} for "{searchQuery}"
          </h2>
          <div className="space-y-3">
            {articles.map((article) => (
              <Link key={article.id} to={`/help/${article.slug}`}>
                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{article.category}</Badge>
                        {article.subcategory && (
                          <Badge variant="secondary">{article.subcategory}</Badge>
                        )}
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{article.title}</h3>
                      <p className="text-muted-foreground">{article.summary}</p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">Browse by Category</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => {
              const Icon = CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS] || HelpCircle;
              const gradient = CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] || "from-gray-500/20 to-gray-500/5";
              const count = getCategoryArticleCount(category);

              return (
                <Card
                  key={category}
                  className={`p-6 hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br ${gradient}`}
                  onClick={() => setSearchQuery(category)}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-background rounded-lg">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-1">{category}</h3>
                      <p className="text-sm text-muted-foreground">
                        {count} {count === 1 ? "article" : "articles"}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Still Need Help */}
      <Card className="p-8 text-center bg-gradient-to-br from-primary/10 to-primary/5">
        <h2 className="text-2xl font-semibold mb-2">Still need help?</h2>
        <p className="text-muted-foreground mb-4">
          Ask Bridgette, your AI assistant, for personalized help
        </p>
        <Link to="/agent">
          <Button size="lg">
            <Cpu className="mr-2 h-4 w-4" />
            Chat with Bridgette
          </Button>
        </Link>
      </Card>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">
          Loading help articles...
        </div>
      )}
    </div>
  );
}
